/**
 * The device's own storage: the IndexedDB plumbing, and the offline read cache
 * built on it.
 *
 * This module imports nothing. It sits underneath both the outbox and the API
 * client so that the API client can clear the cache on sign-out without the two
 * of them importing each other.
 *
 * Two stores:
 *   outbox  pending mutations, oldest first (owned by outbox.ts)
 *   cache   the reference bundle and today's route, so a device that loses signal
 *           mid-route still has grade sheets, prices and its farmer list
 *   photos  pictures taken offline, waiting for a network (owned by photos.ts)
 */

const DB_NAME = 'ledger'
const DB_VERSION = 2

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('outbox')) {
        db.createObjectStore('outbox', { keyPath: 'clientUuid' })
      }
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' })
      }
      // Photographs taken with no signal. The bytes wait here until there is a
      // network, because a scale photo is worthless if it only exists on a phone
      // that goes flat on the way back.
      if (!db.objectStoreNames.contains('photos')) {
        db.createObjectStore('photos', { keyPath: 'localId' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

export function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode)
        const req = work(t.objectStore(store))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

// ---------------------------------------------------------------- cache
/**
 * Everything in here is somebody's data — a route with the farmers on it and what
 * each was paid — so it is scoped to the person who fetched it and cleared when they
 * sign out. These phones are shared: an agent hands one to a driver, or a farmer
 * borrows it, and the next person must not find the last person's round on screen.
 */
let cacheOwner: string | null = null

export function setCacheOwner(userId: string | null) {
  cacheOwner = userId
}

const scoped = (key: string) => `${cacheOwner ?? 'anon'}::${key}`

export const cache = {
  async set(key: string, value: unknown) {
    // Nobody signed in means nobody to own it; do not write it down at all.
    if (!cacheOwner) return
    await tx('cache', 'readwrite', (s) => s.put({ key: scoped(key), value, at: Date.now() }))
  },
  async get<T>(key: string): Promise<T | null> {
    if (!cacheOwner) return null
    const row = await tx<{ value: T } | undefined>('cache', 'readonly', (s) => s.get(scoped(key)))
    return row ? row.value : null
  },
  /** Called on sign-out. Takes the whole store, not just this user's rows. */
  async clear() {
    await tx('cache', 'readwrite', (s) => s.clear())
  },
}
